import { useCallback } from "react";
import {
  selectAssessmentSidebarAddedAt,
  selectAssessmentSidebarCollapsed,
  selectAssessmentSidebarIdsCount,
  selectAssessmentSidebarOpen,
  useAppStore,
} from "../../stores";
import { AssessmentSidebar } from "./AssessmentSidebar";

interface AssessmentSidebarHostProps {
  assessmentSidebarIds: string[];
  assessmentSidebarSelectedIds: string[];
  regulationTitles: Record<string, string>;
  regulationVersions: Record<string, string>;
  regulationMarkets: Record<string, string>;
  regulationProductTypes: Record<string, string>;
  canRender: boolean;
  canRunCompanyScope: boolean;
  canRunCustomScope: boolean;
  companyScopeLoadingRegulationIds: string[];
  onCompanyScopeImpact: (ids: string[]) => void;
  onCustomScopeImpact: () => void;
}

export function AssessmentSidebarHost({
  assessmentSidebarIds,
  assessmentSidebarSelectedIds,
  regulationTitles,
  regulationVersions,
  regulationMarkets,
  regulationProductTypes,
  canRender,
  canRunCompanyScope,
  canRunCustomScope,
  companyScopeLoadingRegulationIds,
  onCompanyScopeImpact,
  onCustomScopeImpact,
}: Readonly<AssessmentSidebarHostProps>) {
  const assessmentSidebarOpen = useAppStore(selectAssessmentSidebarOpen);
  const assessmentSidebarCollapsed = useAppStore(selectAssessmentSidebarCollapsed);
  const assessmentSidebarIdsCount = useAppStore(selectAssessmentSidebarIdsCount);
  const assessmentSidebarAddedAt = useAppStore(selectAssessmentSidebarAddedAt);
  const toggleAssessmentSidebarCollapsed = useAppStore(
    (state) => state.toggleAssessmentSidebarCollapsed,
  );
  const removeAssessmentSidebarId = useAppStore(
    (state) => state.removeAssessmentSidebarId,
  );
  const toggleAssessmentSidebarSelectedId = useAppStore(
    (state) => state.toggleAssessmentSidebarSelectedId,
  );
  const clearAssessmentSidebarIds = useAppStore(
    (state) => state.clearAssessmentSidebarIds,
  );

  const handleCompanyScopeImpact = useCallback(() => {
    onCompanyScopeImpact(assessmentSidebarSelectedIds);
  }, [assessmentSidebarSelectedIds, onCompanyScopeImpact]);

  if (!assessmentSidebarOpen || !canRender) return null;

  const hasSelectedRegulations = assessmentSidebarSelectedIds.length > 0;

  return (
    <AssessmentSidebar
      assessmentSidebarIds={assessmentSidebarIds}
      assessmentSidebarSelectedIds={assessmentSidebarSelectedIds}
      assessmentSidebarCollapsed={assessmentSidebarCollapsed}
      assessmentSidebarIdsCount={assessmentSidebarIdsCount}
      regulationTitles={regulationTitles}
      regulationVersions={regulationVersions}
      regulationMarkets={regulationMarkets}
      regulationProductTypes={regulationProductTypes}
      regulationAddedAt={assessmentSidebarAddedAt}
      onToggleCollapsed={toggleAssessmentSidebarCollapsed}
      onRemoveId={removeAssessmentSidebarId}
      onClearAll={clearAssessmentSidebarIds}
      onToggleSelectedId={toggleAssessmentSidebarSelectedId}
      onCompanyScopeImpact={
        canRunCompanyScope && hasSelectedRegulations
          ? handleCompanyScopeImpact
          : undefined
      }
      onCustomScopeImpact={
        canRunCustomScope && hasSelectedRegulations ? onCustomScopeImpact : undefined
      }
      companyScopeStarting={companyScopeLoadingRegulationIds.some((id) =>
        assessmentSidebarSelectedIds.includes(id),
      )}
    />
  );
}

